import {logger, ObjUtil, Util} from 'yougile-util'
import {Entity} from 'yougile-model'
import {Tag} from 'yougile-model'
import {DataObjectId} from 'yougile-model'
import {Hub} from 'yougile-model'
import {DepartmentsClient} from '@client/departments-client'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {PermissionsChecker} from '../client/permissions-checker'
import {Sync} from '../client/sync'
import {SyncEvents} from '../client/sync-events'

/**
 * Allows to add an order of entities to some entity.
 * It adds tag #.order to orderHostPath and connects it to
 * itemPath (path of the entity) and the order of entities is represented
 * by list that is stored in the connection (#.order > entity) modifier.
 * By default it sorts allEntities items by timestamp, but then it is possible
 * to change order and save it.
 * It has also method handleDrop to automatically handle drop event from
 * drag-list component.
 * It was not made with class because it passed to angular
 * and therefore loses 'this' context.
 */
export class Order {
  public companyId: DataObjectId
  private orderList: Entity[] = []
  private orderHostId: DataObjectId = undefined
  private itemId: DataObjectId = undefined
  private listenerId: number = undefined

  /**
   * Initializes order structure for the specified order item and order host.
   */
  public static init(
    orderHost: Hub,
    item: Entity,
    allEntities: Entity[]
  ): void {
    const order = new Order(item.companyId)
    order.load(orderHost.id, item.id, allEntities)
    order.save()
  }

  constructor(companyId: DataObjectId) {
    this.companyId = companyId
  }

  /**
   * This method can be passed around and be executed in different context
   */
  public order = (entity: Entity): number => {
    return this.orderList.indexOf(entity)
  }

  /**
   * This method can be passed around and be executed in different context
   */
  public handleDrop: (ix: number, entity: Entity) => void = (
    ix: number,
    entity: Entity
  ): void => {
    this.cleanDeleted()
    if (ix !== this.orderList.indexOf(entity)) {
      if (this.orderList.includes(entity)) {
        this.orderList.splice(this.orderList.indexOf(entity), 1)
      }
      this.orderList.splice(ix, 0, entity)
      this.save()
    }
  }

  public insertBefore: (entity: Entity, before: Entity) => void = (
    entity,
    before
  ) => {
    this.cleanDeleted()
    if (entity !== before) {
      const eix = this.orderList.indexOf(entity)
      if (eix !== -1) {
        this.orderList.splice(eix, 1)
      }

      const bix = this.orderList.indexOf(before)
      if (bix !== -1) {
        this.orderList.splice(bix, 0, entity)
        this.save()
      }
    }
  }

  public insertAfter: (entity: Entity, after: Entity) => void = (
    entity,
    after
  ) => {
    this.cleanDeleted()
    if (entity !== after) {
      const eix = this.orderList.indexOf(entity)
      if (eix !== -1) {
        this.orderList.splice(eix, 1)
      }

      const aix = this.orderList.indexOf(after)
      if (aix !== -1) {
        this.orderList.splice(aix + 1, 0, entity)
        this.save()
      }
    }
  }

  public checkAndFixEntities(): void {
    let changed = false
    for (let i = this.orderList.length - 1; i >= 0; i--) {
      const ent = this.orderList[i]
      if (!!ent && ent.location !== this.itemId) {
        logger.warn(
          'Removing incorrect order entity',
          this.orderList[i].title,
          Sync.get(this).get(this.itemId).title
        )
        this.orderList.splice(i, 1)
        changed = true
      }
    }
    if (changed) {
      this.save()
    }
  }

  public cleanDeleted(): void {
    const len = this.orderList.length
    this.orderList = this.orderList.filter(
      e => !e.deleted && e.location === this.orderHostId
    )
    if (this.orderList.length !== len) {
      this.save()
    }
  }

  public getList(): Entity[] {
    const box = Sync.get({companyId: this.companyId})
    const calculatedHierarchy = DepartmentsClient.getHierarchy(box)
    return (this.orderList || []).filter(
      e =>
        !!e &&
        (!(e instanceof Hub) ||
          PermissionsChecker.checkShow('ignore-departments', e, {
            calculatedHierarchy
          }))
    )
  }

  public setEntities(list: Entity[]): void {
    this.orderList = [...list]
    this.save()
  }

  public addEntity(entity: Entity, toEnd: boolean): void {
    if (!toEnd) {
      this.orderList.unshift(entity)
    } else {
      this.orderList.push(entity)
    }
    this.save()
  }

  public removeEntity(entity: Entity): void {
    const ix = this.orderList.indexOf(entity)
    if (ix === -1) {
      logger.error('Entity not found')
      return
    }
    this.orderList.splice(ix, 1)
    this.save()
  }

  private isIncorrectOrder(): boolean {
    const box = Sync.get(this)
    const item = box.get(this.itemId)
    const isIncorrect = item instanceof Hub && box.getType(item) === 'task'
    if (isIncorrect) {
      logger.log('Incorrect order item (task)', this.itemId)
    }
    return isIncorrect
  }

  public load(
    orderHostId: DataObjectId,
    itemId: DataObjectId,
    allEntities: Entity[]
  ): void {
    if (this.isIncorrectOrder()) {
      return
    }
    Util.assert(!!allEntities, 'All entities must be defined')
    this.orderHostId = orderHostId
    this.itemId = itemId
    const box = Sync.get(this)
    if (!box.hasChildWithTitle(this.orderHostId, '.order', 'Tag')) {
      box.add(new Tag(this.companyId, '.order'), this.orderHostId)
    }
    const orderTagId = box.findIdByTitle('Tag', '.order', this.orderHostId)
    if (box.isConnected(orderTagId, this.itemId)) {
      this.orderList = Util.removeDuplicateIds(
        box
          .getModifiers(orderTagId, this.itemId)
          .filter(
            (id: DataObjectId) =>
              box.has(id) && box.get(id) && !box.get(id)?.deleted
          )
          .map((id: DataObjectId) => box.get(id))
          .filter(ObjUtil.isDefined)
      )
      const ix = new Set(this.orderList)

      const absent = allEntities
        .filter((h: Entity) => !ix.has(h))
        .sort((a: Entity, b: Entity) => (a.timestamp < b.timestamp ? 1 : -1))
      this.orderList.splice(0, 0, ...absent)
    } else {
      this.orderList = allEntities.sort((a: Entity, b: Entity) =>
        a.timestamp < b.timestamp ? 1 : -1
      )
      logger.log('Not initialized order, saving', itemId)
      this.save()
    }
    if (this.listenerId) {
      SyncEvents.removeListener(this.listenerId)
    }
    this.listenerId = SyncEvents.addListener(this.companyId, orderTagId, () => {
      this.load(orderHostId, itemId, this.orderList)
    })
  }

  cleanup(): void {
    SyncEvents.removeListener(this.listenerId)
  }

  public save(): void {
    if (SharedBoardsClient.getId()) {
      return
    }
    if (this.isIncorrectOrder()) {
      return
    }
    const box = Sync.get(this)
    if (!box.hasChildWithTitle(this.orderHostId, '.order', 'Tag')) {
      box.add(new Tag(this.companyId, '.order'), this.orderHostId)
    }
    const orderTagId = box.findIdByTitle('Tag', '.order', this.orderHostId)
    if (!box.isConnected(orderTagId, this.itemId)) {
      box.connect(orderTagId, this.itemId)
    }
    box.setModifiers(
      orderTagId,
      this.itemId,
      this.orderList.map((h: Entity) => h?.id).filter(ObjUtil.isDefined)
    )
  }
}
